import { HttpMethodEnum } from '@ipevo-rd/service-web-utils'
import { adminHttpClient } from './client.js'
import type {
	DashboardOverview,
	DashboardChannels,
	DashboardChannelsQuery,
	DashboardTrends,
	LanguageOverviewResponse,
} from './dto.js'

/**
 * 共用 GET 請求：發送、檢查業務碼、回傳 data
 * @param url 含 query 的完整路徑
 * @returns 對應型別的資料
 */
const getJson = async <T>(url: string): Promise<T | undefined> => {
	// 透過 admin client 發送請求取得 body
	const body = await adminHttpClient.request<T>({
		url,
		method: HttpMethodEnum.GET,
	})

	// 回傳對應型別的資料
	return body.data
}

/**
 * 將起訖日期塞進 URLSearchParams（有值才塞）
 * @param params 目標 URLSearchParams
 * @param startDate 起始時間字串（可選）
 * @param endDate 結束時間字串（可選）
 */
const appendDateRange = (
	params: URLSearchParams,
	startDate?: string,
	endDate?: string,
) => {
	// 有起始日才帶
	if (startDate) {
		params.append('start_date', startDate)
	}

	// 有結束日才帶
	if (endDate) {
		params.append('end_date', endDate)
	}
}

/**
 * 取得平台總覽卡片（A-1）
 * @param startDate 起始時間字串（只影響區間內新建頻道數，可選）
 * @param endDate 結束時間字串（可選）
 * @returns 平台總覽資料
 */
export const getDashboardOverview = (startDate?: string, endDate?: string) => {
	// 組合查詢參數
	const params = new URLSearchParams()

	// 塞入起訖日期
	appendDateRange(params, startDate, endDate)

	// 發送請求取得平台總覽資料
	return getJson<DashboardOverview>(
		`/admin/dashboard/overview?${params.toString()}`,
	)
}

/**
 * 取得頻道排行榜 / 列表（A-2）
 * @param query 排序、分頁與日期過濾參數
 * @returns 頻道排行榜資料（含總筆數）
 */
export const getDashboardChannels = (query: DashboardChannelsQuery = {}) => {
	// 組合查詢參數
	const params = new URLSearchParams()

	// 有排序欄位才帶
	if (query.sort_by) {
		params.append('sort_by', query.sort_by)
	}
	// 有排序方向才帶
	if (query.order) {
		params.append('order', query.order)
	}
	// 有筆數上限才帶
	if (query.limit !== undefined) {
		params.append('limit', String(query.limit))
	}
	// 有位移才帶
	if (query.offset !== undefined) {
		params.append('offset', String(query.offset))
	}

	// 塞入起訖日期
	appendDateRange(params, query.start_date, query.end_date)

	// 發送請求取得頻道排行榜資料
	return getJson<DashboardChannels>(
		`/admin/dashboard/channels?${params.toString()}`,
	)
}

/**
 * 取得時間趨勢（A-3）
 * @param startDate 區間起（必填）
 * @param endDate 區間迄（必填）
 * @param granularity 週期 day / week（可選，預設 day）
 * @returns 時間趨勢資料
 */
export const getDashboardTrends = (
	startDate: string,
	endDate: string,
	granularity?: 'day' | 'week',
) => {
	// 組合查詢參數（起訖必帶）
	const params = new URLSearchParams()
	// 帶上 start_date 查詢參數
	params.append('start_date', startDate)
	// 帶上 end_date 查詢參數
	params.append('end_date', endDate)

	// 有週期才帶
	if (granularity) {
		params.append('granularity', granularity)
	}

	// 發送請求取得時間趨勢資料
	return getJson<DashboardTrends>(
		`/admin/dashboard/trends?${params.toString()}`,
	)
}

/**
 * 取得語言供需總覽
 * @param startDate 起始時間字串（只影響需求側 viewer_count，可選）
 * @param endDate 結束時間字串（可選）
 * @returns 各語言的供給頻道數與需求觀眾數，依語言代號升冪排序
 */
export const getLanguageOverview = (startDate?: string, endDate?: string) => {
	// 組合查詢參數
	const params = new URLSearchParams()

	// 塞入起訖日期
	appendDateRange(params, startDate, endDate)

	// 發送請求取得語言供需總覽資料
	return getJson<LanguageOverviewResponse>(
		`/admin/dashboard/languages?${params.toString()}`,
	)
}
